import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Star, MapPin, Briefcase, Calendar, MessageCircle, CheckCircle2, GitCompare, Loader2, Download } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { ChannelBadge } from "@/components/ChannelBadge";

export default function CompanyShortlist() {
  const [shortlists, setShortlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareIds, setCompareIds] = useState([]);

  useEffect(() => {
    api.get("/shortlists/me").then(r => setShortlists(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const requestCall = async (manager_id, request_id, company_id) => {
    try {
      await api.post("/meetings/", {
        type: "discovery",
        manager_id, request_id, company_id,
        date: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
        time: "15:00", duration_minutes: 30, participants: [],
        notes: "Richiesta call conoscitiva dalla shortlist",
      });
      toast.success("Richiesta call inviata al team CXOORA");
    } catch (e) { toast.error("Errore richiesta call"); }
  };

  const toggleCompare = (id) => {
    setCompareIds(arr => arr.includes(id) ? arr.filter(x => x !== id) : (arr.length < 3 ? [...arr, id] : arr));
  };

  if (loading) return <Loader2 className="animate-spin text-slate-400" />;

  if (!shortlists.length) {
    return (
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Shortlist</h1>
        <div className="cxo-card p-10 text-center mt-4">
          <p className="text-slate-500">Non hai ancora una shortlist disponibile. Il team CXOORA sta lavorando alla tua selezione.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Le tue shortlist</h1>
          <p className="text-sm text-slate-600">Esplora i manager selezionati per te. Confronta fino a 3 profili.</p>
        </div>
        {compareIds.length >= 2 && (
          <ComparisonDialog shortlists={shortlists} compareIds={compareIds} reset={() => setCompareIds([])} />
        )}
      </div>

      {shortlists.map(sl => (
        <div key={sl.id} className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ChannelBadge channel={sl.channel || "agency"} size="xs" />
                {sl.auto_generated && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">Auto-generata</Badge>}
              </div>
              <h2 className="font-display text-lg font-semibold text-slate-900">{sl.request?.title || "Richiesta"}</h2>
              <p className="text-xs text-slate-500">{sl.notes}</p>
            </div>
            <a
              href={`${API_BASE}/pdf/shortlist/${sl.id}?auth=${encodeURIComponent(localStorage.getItem("cxoora_token") || "")}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`download-shortlist-pdf-${sl.id}`}
            >
              <Button size="sm" variant="outline" className="gap-1.5">
                <Download size={14} /> Scarica PDF
              </Button>
            </a>
          </div>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="shortlist-grid">
            {sl.managers?.map(m => (
              <div key={m.id} className="cxo-card p-5 flex flex-col">
                <div className="flex items-start gap-3">
                  <img src={m.photo} alt={m.user?.full_name} className="w-14 h-14 rounded-full object-cover" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{m.user?.full_name}</h3>
                    <p className="text-xs text-slate-600 truncate">{m.title}</p>
                  </div>
                  <Checkbox checked={compareIds.includes(m.id)} onCheckedChange={() => toggleCompare(m.id)} data-testid={`compare-checkbox-${m.id}`} />
                </div>
                <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-[#EAF0FF] to-white border border-[#2D6BFF]/20">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#2D6BFF]">Match score</span>
                    <span className="font-display text-3xl font-bold cxo-tabular text-[#2D6BFF]">{m.match_score || 0}%</span>
                  </div>
                </div>
                <ul className="mt-3 space-y-1">
                  {(m.match_reasons || []).map((r, i) => (
                    <li key={i} className="flex gap-2 text-xs text-slate-600"><CheckCircle2 size={14} className="text-[#1F9D6A] shrink-0 mt-0.5" />{r}</li>
                  ))}
                </ul>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(m.badges || []).slice(0, 3).map(b => <Badge key={b} variant="secondary" className="bg-slate-100 text-slate-700 border-0">{b}</Badge>)}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                  <span className="inline-flex gap-1"><MapPin size={12} className="shrink-0 mt-0.5" /> {m.city || m.region}</span>
                  <span className="inline-flex gap-1"><Briefcase size={12} className="shrink-0 mt-0.5" /> {m.years_managerial || 0}+ anni</span>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                  <Button size="sm" className="flex-1 bg-[#2D6BFF] text-white" onClick={() => requestCall(m.id, sl.request_id, sl.company_id)} data-testid={`request-call-${m.id}`}>
                    <Calendar size={14} className="mr-1" /> Richiedi call
                  </Button>
                  <ManagerDetailDialog manager={m} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const ManagerDetailDialog = ({ manager }) => (
  <Dialog>
    <DialogTrigger asChild>
      <Button size="sm" variant="outline" data-testid={`view-profile-${manager.id}`}>Profilo</Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center gap-3">
          <img src={manager.photo} alt="" className="w-16 h-16 rounded-full object-cover" />
          <div>
            <DialogTitle>{manager.user?.full_name}</DialogTitle>
            <p className="text-sm text-slate-600">{manager.title}</p>
          </div>
        </div>
      </DialogHeader>
      <div className="space-y-4">
        <p className="text-sm text-slate-700">{manager.bio}</p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <DetailRow label="Ruolo principale" value={manager.primary_role} />
          <DetailRow label="Esperienza" value={`${manager.years_managerial || 0}+ anni manageriali`} />
          <DetailRow label="Settori" value={(manager.industries || []).join(", ")} />
          <DetailRow label="Competenze" value={(manager.skills || []).join(", ")} />
          <DetailRow label="Disponibilità" value={`${manager.monthly_availability_days || 0} gg/mese`} />
          <DetailRow label="Modalità" value={(manager.work_modes || []).join(", ")} />
          <DetailRow label="Città" value={manager.city} />
          <DetailRow label="Lingue" value={(manager.languages || []).join(", ")} />
        </div>
        {manager.case_studies?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2">Case study</p>
            {manager.case_studies.map((cs, i) => (
              <div key={i} className="p-3 rounded-lg bg-slate-50 mb-2">
                <p className="font-medium text-slate-900">{cs.title}</p>
                <p className="text-xs text-slate-600 mt-1">{cs.description}</p>
                {cs.result && <Badge className="mt-2 bg-[#E9F8F1] text-[#1F9D6A] border-0">Risultato: {cs.result}</Badge>}
              </div>
            ))}
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const DetailRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
    <p className="font-medium text-slate-900">{value || "—"}</p>
  </div>
);

const ComparisonDialog = ({ shortlists, compareIds, reset }) => {
  const allMgrs = shortlists.flatMap(sl => sl.managers || []);
  const selected = compareIds.map(id => allMgrs.find(m => m?.id === id)).filter(Boolean);
  return (
    <Dialog defaultOpen onOpenChange={(o) => { if (!o) reset(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confronto manager ({selected.length})</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr>
                <th className="text-left p-2"></th>
                {selected.map(m => (
                  <th key={m.id} className="p-2 text-left">
                    <img src={m.photo} className="w-12 h-12 rounded-full mb-2" alt="" />
                    <p className="font-semibold">{m.user?.full_name}</p>
                    <p className="text-xs text-slate-500 font-normal">{m.title}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[["Match score", m => `${m.match_score}%`], ["Ruolo principale", m => m.primary_role], ["Esperienza", m => `${m.years_managerial}+ anni`], ["Settori", m => (m.industries || []).join(", ")], ["Competenze", m => (m.skills || []).slice(0, 5).join(", ")], ["Disponibilità", m => `${m.monthly_availability_days} gg/mese`], ["Modalità", m => (m.work_modes || []).join(", ")], ["Città", m => m.city], ["Lingue", m => (m.languages || []).join(", ")]].map(([k, fn]) => (
                <tr key={k} className="border-t border-slate-100">
                  <td className="p-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{k}</td>
                  {selected.map(m => <td key={m.id} className="p-2">{fn(m)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
